/**
 * Keep the screen on while `active`. In the Airview Studio iOS app: the app's idle timer
 * (getNative().keepAwake). In a browser: the Screen Wake Lock API (iOS Safari 16.4+,
 * Chrome/Android) — released automatically when the tab is hidden; we re-acquire on return.
 * Web pages lose GPS when the phone locks, so field views hold the screen awake while sharing.
 */
import { useEffect, useState } from 'react';
import { getNative } from './native';

type Sentinel = { release(): Promise<void>; addEventListener?(t: 'release', f: () => void): void };
type WakeLockApi = { request(type: 'screen'): Promise<Sentinel> };

export function wakeLockSupported(): boolean {
  return getNative() !== null || (typeof navigator !== 'undefined' && 'wakeLock' in navigator);
}

export function useKeepAwake(active: boolean): { held: boolean; supported: boolean } {
  const [held, setHeld] = useState(false);
  const supported = wakeLockSupported();
  useEffect(() => {
    if (!active || !supported) return;
    const native = getNative();
    if (native) {
      native.keepAwake(true);
      setHeld(true);
      return () => {
        native.keepAwake(false);
        setHeld(false);
      };
    }
    let sentinel: Sentinel | null = null;
    let cancelled = false;
    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible' || sentinel) return;
      try {
        sentinel = await (navigator as unknown as { wakeLock: WakeLockApi }).wakeLock.request('screen');
        setHeld(true);
        sentinel.addEventListener?.('release', () => {
          sentinel = null;
          setHeld(false);
        });
      } catch {
        setHeld(false); // denied (low battery, not visible) — harmless
      }
    };
    const onVis = () => void acquire();
    void acquire();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
      setHeld(false);
    };
  }, [active, supported]);
  return { held, supported };
}
